import { useCallback, useEffect, useState } from 'react'
import {
  WEAR_LOGGED_EVENT,
  getDeferredInstallPrompt,
  promptDeferredInstall,
  startInstallPromptCapture,
  isIOSSafari,
  isInstallPromptDismissed,
  isStandalone,
  markInstallPromptDismissed,
} from '../lib/pwaInstall'

/**
 * Decides whether to offer "add to home screen", and when.
 *
 * Trigger = after the first successful wear log, NOT on first load. Asking on
 * load is what everyone does and it is why everyone's dismiss rate is ~90%:
 * the ask arrives before the user has any reason to say yes. After a wear log
 * they have felt the loop, and "get your daily nudge" is a sentence that now
 * means something. Ask at peak motivation (Fogg), not at peak convenience.
 *
 * One ask, ever. Dismiss is remembered.
 */
export function useInstallPrompt() {
  const [visible, setVisible] = useState(false)
  const [mode, setMode] = useState(null) // 'native' | 'ios-manual'

  useEffect(() => {
    // The native prompt event is captured once, app-wide (pwaInstall), so the
    // on-demand guide and this prompt never hold diverging copies of it.
    startInstallPromptCapture()
    // If they install by any route (our button, browser menu, whatever),
    // never ask again.
    const onAppInstalled = () => {
      setVisible(false)
      markInstallPromptDismissed()
    }
    window.addEventListener('appinstalled', onAppInstalled)
    return () => window.removeEventListener('appinstalled', onAppInstalled)
  }, [])

  useEffect(() => {
    const onWearLogged = () => {
      if (isStandalone() || isInstallPromptDismissed()) return

      const canNative = getDeferredInstallPrompt() != null
      const canIOSManual = isIOSSafari()
      // Nothing actionable to offer: desktop browsers with no install support,
      // Chrome-on-iOS, in-app webviews. Staying silent beats telling someone
      // to tap a Share button that will not help them.
      if (!canNative && !canIOSManual) return

      setMode(canNative ? 'native' : 'ios-manual')
      // Let the save confirmation land first — do not stack a modal on a modal.
      window.setTimeout(() => setVisible(true), 700)
    }
    window.addEventListener(WEAR_LOGGED_EVENT, onWearLogged)
    return () => window.removeEventListener(WEAR_LOGGED_EVENT, onWearLogged)
  }, [])

  const promptInstall = useCallback(async () => {
    try {
      // null when another surface already used the single-use prompt.
      const outcome = await promptDeferredInstall()
      setVisible(false)
      if (outcome) markInstallPromptDismissed() // asked once; respect the answer either way
      return outcome
    } catch {
      setVisible(false)
      return null
    }
  }, [])

  const dismiss = useCallback(() => {
    markInstallPromptDismissed()
    setVisible(false)
  }, [])

  return { visible, mode, promptInstall, dismiss }
}
